import { AsyncAutocomplete, Button, ListItem, ListItemText, Stack } from '@cmg/design-system';
import React from 'react';

import ServerErrorsBanner from '../../../../../../../common/components/indicators/server-error/ServerErrorsBanner';
import { InvestorContact } from '../InvestorContactInformationForm.model';
import { useInvestorContactSearch_InvestorFirmsEmployeesQuery } from './graphql/__generated__';
import { getContactsOptions, InvestorContactOption } from './InvestorContactSearch.model';
import { NoOptions } from './no-options/NoOptions';

export type Props = {
  handleAddContact: (item: InvestorContact) => void;
  bankInvestorKey: string | undefined;
  disabled?: boolean;
  isCoveredAccount: boolean;
  existingContacts: InvestorContact[];
};

const InvestorContactSearch: React.FC<Props> = ({
  handleAddContact,
  bankInvestorKey,
  disabled,
  isCoveredAccount,
  existingContacts,
}) => {
  const { data, error, loading } = useInvestorContactSearch_InvestorFirmsEmployeesQuery({
    variables: {
      firmKey: bankInvestorKey!,
      isCoveredAccount,
    },
    skip: !bankInvestorKey,
  });

  const investorContacts =
    React.useMemo(() => {
      if (!data) {
        return [];
      }

      const grid = isCoveredAccount ? data.coveredInvestorFirmsGrid : data.investorFirmsGrid;
      return grid?.items?.[0]?.investorContacts?.items ?? [];
    }, [data, isCoveredAccount]) ?? [];

  const noOptionsComponent = React.useMemo(() => {
    return (
      <NoOptions
        handleCreateNew={() => handleAddContact({ name: '', email: '', isCustom: true })}
      />
    );
  }, [handleAddContact]);

  return (
    <Stack>
      {error && <ServerErrorsBanner error={error} />}
      <AsyncAutocomplete<InvestorContactOption, false, false>
        onFetchOptions={searchedText => {
          return Promise.resolve({
            data: getContactsOptions(investorContacts).filter(val => {
              const lowerCaseSearchedText = searchedText.toLowerCase();
              return (
                val.label.toLowerCase().includes(lowerCaseSearchedText) ||
                val.email.toLowerCase().includes(lowerCaseSearchedText)
              );
            }),
          });
        }}
        TextFieldProps={{
          name: 'investorContact',
          label: 'Investor Contact',
          placeholder: 'Select a contact...',
        }}
        sx={{ width: '60%', marginBottom: theme => theme.spacing(2) }}
        disabled={disabled}
        isLoadingOptions={loading}
        value={null}
        footerButton={
          <Button
            variant="text"
            aria-label="create new footer"
            onClick={() => {
              handleAddContact({
                name: '',
                email: '',
                isCustom: true,
              });
            }}
          >
            Create New
          </Button>
        }
        onChange={(_, value) => {
          if (!value) {
            return;
          }

          const selectedValue = typeof value === 'string' ? value : value.value;
          const contact = investorContacts.find(item => item.investorEmployeeId === selectedValue);

          if (contact) {
            handleAddContact({
              name: contact.name,
              email: contact.email1 ?? contact.email2 ?? '',
              isCustom: false,
            });
          }
        }}
        blurOnSelect={true}
        renderOption={(props, option, _, ownerState) => {
          if (
            existingContacts.some(
              contact => contact.name === option.label && contact.email === option.email
            )
          ) {
            return undefined;
          }
          const { key, ...rest } = props;
          return (
            <ListItem key={key} {...rest} disablePadding>
              <ListItemText primary={ownerState.getOptionLabel(option)} secondary={option.email} />
            </ListItem>
          );
        }}
        noOptionsText={noOptionsComponent}
      ></AsyncAutocomplete>
    </Stack>
  );
};

export default InvestorContactSearch;
